// frontend/src/context/AuthContext.js
import React, { createContext, useState, useEffect, useCallback, useRef, useContext, useMemo } from 'react';
import debounce from 'lodash.debounce';
import api from '../utils/api';
import { useNavigate, useLocation } from 'react-router-dom';
import { message } from 'antd';
import { ROLE_DEFINITIONS, ROLE_NAMES, ROLE_IDS, ROLES, PERMISSION_LEVELS } from '../const/roles';

export { ROLE_DEFINITIONS, ROLE_NAMES, ROLE_IDS, ROLES, PERMISSION_LEVELS };

export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

const toCamelCase = (obj) => {
  if (obj === null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(v => toCamelCase(v));
  return Object.keys(obj).reduce((acc, key) => {
    const camelKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
    acc[camelKey] = toCamelCase(obj[key]);
    return acc;
  }, {});
};

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState({ user: null, isAuthenticated: false });
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState(null);

  const [tokenExpiry, setTokenExpiry] = useState(() => {
    // Initialize from localStorage on mount
    const saved = localStorage.getItem('tokenExpiry');
    return saved ? parseInt(saved) : null;
  });

  // Client Computing Portal — EUC contracts (populated after login)
  const [eucContracts, setEucContracts] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();
  const refreshTokenTimeout = useRef(null);
  const isCheckingRef = useRef(false);
  const lastCheckRef = useRef(0);

  const log = useCallback((level, message, data = {}) => {
    const styles = {
      info: 'color: gray;',
      success: 'color: green;',
      warn: 'color: orange;',
      error: 'color: red;'
    };
    console.log(`%cAuthContext: ${message}`, styles[level] || '', data);
  }, []);

  const processAndSetUser = useCallback((rawUserData) => {
    console.log('AuthContext: processAndSetUser raw data:', rawUserData);

    // CRITICAL: Log the main contractor fields immediately
    console.log('AuthContext: isMainContractor from rawUserData:', rawUserData.isMainContractor);
    console.log('AuthContext: mainContractorContractIds from rawUserData:', rawUserData.mainContractorContractIds);
    console.log('AuthContext: mainContractorName from rawUserData:', rawUserData.mainContractorName);

    if (!rawUserData) {
      log('warn', 'Clearing user state');
      setAuth({ user: null, isAuthenticated: false });
      localStorage.removeItem('user');
      return null;
    }

    // Normalize roleName to handle 'SYSTEM ADMIN' vs 'SYSTEM_ADMIN'
    const normalizedRoleName = rawUserData.roleName?.replace(' ', '_') || rawUserData.role_name?.replace(' ', '_');
    const roleKey = Object.keys(ROLE_DEFINITIONS).find(
      key => ROLE_DEFINITIONS[key].id === (rawUserData.roleId || rawUserData.role_id) ||
             ROLE_DEFINITIONS[key].name === normalizedRoleName ||
             ROLE_DEFINITIONS[key].name === rawUserData.roleName
    ) || 'USER'; // Default to 'USER' if no match

    const processedUser = {
      ...rawUserData,
      organizationName: rawUserData.organizationName || rawUserData.organization?.name || 'Unknown',
      isActive: rawUserData.isActive ?? rawUserData.status === 'active',
      createdAt: rawUserData.createdAt || rawUserData.created_at || null,
      firstName: rawUserData.firstName || rawUserData.first_name || '',
      lastName: rawUserData.lastName || rawUserData.last_name || '',
      roleId: rawUserData.roleId || rawUserData.role_id || null,
      role: roleKey,
      roleName: rawUserData.roleName || 
                ROLE_DEFINITIONS[roleKey]?.name || 
                (rawUserData.roleId ? 
                  Object.values(ROLE_DEFINITIONS).find(d => d.id === (rawUserData.roleId || rawUserData.role_id))?.name : 
                  'User'),
      userType: rawUserData.userType || rawUserData.user_type || 'Unknown',
      organizationId: rawUserData.organizationId || rawUserData.organization_id || 0,
      isMainContractor: rawUserData.isMainContractor === true ? true : false,
      mainContractorContractIds: rawUserData.mainContractorContractIds || [],
      mainContractorName: rawUserData.mainContractorName || null
    };

    // Log the processed user before saving
    console.log('AuthContext: processedUser before save:', {
      isMainContractor: processedUser.isMainContractor,
      mainContractorContractIds: processedUser.mainContractorContractIds,
      mainContractorName: processedUser.mainContractorName
    });

    localStorage.setItem('user', JSON.stringify(processedUser));
    setAuth({ user: processedUser, isAuthenticated: true });
    
    log('info', 'User processed', {
      id: processedUser.id,
      email: processedUser.email,
      roleId: processedUser.roleId,
      role: processedUser.role,
      roleName: processedUser.roleName,
      userType: processedUser.userType,
      organizationId: processedUser.organizationId,
      organizationName: processedUser.organizationName,
      isMainContractor: processedUser.isMainContractor,
      mainContractorContractIds: processedUser.mainContractorContractIds,
      mainContractorName: processedUser.mainContractorName,
      permissions: ROLE_DEFINITIONS[roleKey]?.permissions || {}
    });
    
    return processedUser;
  }, [log, setAuth, ROLE_DEFINITIONS]);

  const clearAuth = useCallback((options = {}) => {
    const {
      redirect = true,
      messageType = 'info',
      customMessage = 'Session expired. Please log in again.',
      isPermissionIssue = false
    } = options;

    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('tokenExpiry');
    setTokenExpiry(null);
    api.defaults.headers.common['Authorization'] = '';
    setAuth({ user: null, isAuthenticated: false });
    setLoading(false);
    setError(null);
    setEucContracts([]);   // 🆕 Reset EUC contracts on logout/session clear

    if (refreshTokenTimeout.current) {
      clearTimeout(refreshTokenTimeout.current);
      refreshTokenTimeout.current = null;
    }

    if (redirect) {
      log('info', `Auth cleared - redirecting to ${isPermissionIssue ? 'unauthorized' : 'login'}`);
      navigate(isPermissionIssue ? '/unauthorized' : '/login', { 
        replace: true,
        state: { from: isPermissionIssue ? location.pathname : undefined }
      });
      message[messageType]({ content: customMessage, duration: 2 });
    }
  }, [navigate, log, location.pathname]);

  // ============================================================
  // 🆕 Client Computing Portal — fetch EUC contracts
  // Non-blocking, silent-fail (most users won't have EUC access)
  // MUST be defined BEFORE fetchUserProfile (used in its deps)
  // ============================================================
  const fetchClientPortalContracts = useCallback(async () => {
    try {
      const response = await api.get('/api/client/contracts');
      if (response.data?.success) {
        const list = response.data.data || [];
        setEucContracts(list);
        return list;
      }
      setEucContracts([]);
      return [];
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('[AuthContext] No EUC contracts / client portal access:', err.response?.status);
      }
      setEucContracts([]);
      return [];
    }
  }, []);

  const fetchUserProfile = useCallback(async () => {
    try {
      const response = await api.get('/api/users/profile');
      console.log('User profile response:', response.data);
      
      const userData = response.data.user || response.data.data || response.data;
      
      if (!userData || !userData.id) {
        throw new Error('Invalid user data structure');
      }
      
      console.log('Raw user data fields:', Object.keys(userData));
      console.log('Role-related fields:', {
        role: userData.role,
        role_id: userData.role_id,
        roleId: userData.roleId
      });
      
      const camelCaseData = toCamelCase(userData);
      const processedUser = processAndSetUser(camelCaseData);

      // 🆕 Fetch EUC contracts (fire-and-forget)
      fetchClientPortalContracts();

      return processedUser;
    } catch (error) {
      log('error', 'Failed to fetch user profile', error);
      clearAuth({ redirect: false });
      return null;
    }
  }, [log, processAndSetUser, clearAuth, fetchClientPortalContracts]);

  const baseRefreshToken = useCallback(async () => {
    try {
      const storedRefreshToken = localStorage.getItem('refreshToken');
      if (!storedRefreshToken) {
        log('warn', 'No refresh token found');
        clearAuth({ redirect: true });
        return { success: false };
      }

      const response = await api.post('/api/auth/refresh-token', { refreshToken: storedRefreshToken });
      if (response.data.success) {
        const { token, refreshToken: newRefreshToken, expiresIn } = response.data;
        
        localStorage.setItem('authToken', token);
        localStorage.setItem('refreshToken', newRefreshToken);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const expiryTime = Date.now() + (expiresIn * 1000);
        setTokenExpiry(expiryTime);
        localStorage.setItem('tokenExpiry', expiryTime.toString());
        
        log('success', 'Token refreshed successfully');
        return { success: true, token, expiresIn };
      }
      throw new Error(response.data.message || 'Refresh failed');
    } catch (err) {
      log('error', 'Token refresh error', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        clearAuth({ redirect: true, messageType: 'error', customMessage: 'Session expired. Please log in.' });
      }
      return { success: false };
    }
  }, [clearAuth, log]);

  const scheduleTokenRefresh = useCallback((expiresIn = 60) => {
    if (refreshTokenTimeout.current) {
      clearTimeout(refreshTokenTimeout.current);
    }

    const refreshTime = Math.max(
      (expiresIn * 60 * 1000) - (15 * 60 * 1000),
      5 * 60 * 1000
    );

    refreshTokenTimeout.current = setTimeout(async () => {
      const result = await baseRefreshToken();
      if (result.success && result.expiresIn) {
        scheduleTokenRefresh(result.expiresIn);
      }
    }, refreshTime);

    log('info', `Scheduled token refresh in ${Math.round(refreshTime/60000)} minutes`);
  }, [baseRefreshToken, log]);

  const refreshToken = useCallback(async () => {
    const result = await baseRefreshToken();
    if (result.success && result.expiresIn) {
      scheduleTokenRefresh(result.expiresIn);
    }
    return result.success;
  }, [baseRefreshToken, scheduleTokenRefresh]);

  const checkAuth = useCallback(debounce(async (force = false) => {
    const now = Date.now();
    if ((!force && auth.isAuthenticated) || isCheckingRef.current || (now - lastCheckRef.current < 5000)) {
      return;
    }

    isCheckingRef.current = true;
    lastCheckRef.current = now;
    setLoading(true);

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        clearAuth({ redirect: false });
        return;
      }

      const verifyResponse = await api.get('/api/auth/check-token').catch(() => null);
      if (!verifyResponse?.data?.success) {
        const refreshSuccess = await refreshToken();
        if (!refreshSuccess) {
          clearAuth({ redirect: false });
          return;
        }
      }

      const userData = await fetchUserProfile();
      if (userData) {
        setAuth({ user: userData, isAuthenticated: true });
        scheduleTokenRefresh();
      } else {
        clearAuth({ redirect: false });
      }
    } catch (err) {
      log('error', 'Auth check error', err);
      clearAuth({ redirect: false });
    } finally {
      isCheckingRef.current = false;
      setLoading(false);
      setInitialized(true);
    }
  }, 500), [auth.isAuthenticated, clearAuth, refreshToken, fetchUserProfile, log, scheduleTokenRefresh]);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const trimmedEmail = email?.trim();
      if (!trimmedEmail || !password?.trim()) {
        throw new Error('Email and password are required');
      }

      log('info', 'Attempting login', { email: trimmedEmail });
      const response = await api.post('/api/auth/login', { 
        email: trimmedEmail, 
        password: password.trim() 
      });

      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Login failed');
      }

      const { token, refreshToken: newRefreshToken, user: rawUser = {}, expiresIn = 3600 } = response.data;
      const userData = rawUser || response.data.data || {};

      console.log('Raw user data from API:', userData);
      if (!token || !newRefreshToken || !userData?.id) {
        throw new Error('Invalid authentication response');
      }

      const expiryTime = Date.now() + (expiresIn * 1000);
      setTokenExpiry(expiryTime);
      localStorage.setItem('tokenExpiry', expiryTime.toString());

      processAndSetUser({
        ...userData,
        email: userData.email || trimmedEmail
      });

      localStorage.setItem('authToken', token);
      localStorage.setItem('refreshToken', newRefreshToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // 🆕 Fire-and-forget: fetch EUC contracts for Client Portal
      fetchClientPortalContracts();

      setInitialized(true);
      scheduleTokenRefresh(expiresIn);
      
      // Determine which dashboard to navigate to
      const { userType, isMainContractor, mainContractorContractIds } = userData;
      if (userType === 'partner' && isMainContractor === true && mainContractorContractIds?.length > 0) {
          navigate('/dashboard/partner', { replace: true });
      } else {
          navigate('/dashboard', { replace: true });
      }
      return { success: true, user: userData };
    } catch (err) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      
      const errorMessage = err.response?.data?.message || err.message || 'Login failed';
      setError(errorMessage);
      log('error', 'Login failed', { error: errorMessage });
      
      return { 
        success: false, 
        message: errorMessage,
        ...(process.env.NODE_ENV === 'development' && { details: err })
      };
    } finally {
      setLoading(false);
    }
  }, [navigate, log, scheduleTokenRefresh, processAndSetUser, fetchClientPortalContracts]);

  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/logout', { 
        refreshToken: localStorage.getItem('refreshToken') 
      });
      log('info', 'Logout successful');
    } catch (err) {
      log('error', 'Logout error', err);
    } finally {
      clearAuth({ redirect: true });
    }
  }, [clearAuth, log]);

  const requestPasswordReset = useCallback(async (email) => { 
    setLoading(true);
    setError(null);
    console.log('AuthContext: Request password reset for:', email);
    try {
      const response = await api.post('/api/auth/forgot-password', { email });
      if (!response.data?.success) {
        console.error('AuthContext: Forgot password failed: No success flag or message.');
        throw new Error(response.data?.message || 'Failed to send reset email');
      }
      console.log('AuthContext: Password reset email request successful.');
      message.success(response.data?.message || 'Password reset link sent to your email.'); 
      return response.data;
    } catch (error) {
      console.error('AuthContext: requestPasswordReset error:', error.response?.data || error.message, error);
      setError(error.response?.data?.message || error.message);
      message.error(error.response?.data?.message || 'Failed to send password reset link.'); 
      throw error;
    } finally {
      setLoading(false);
    }
  }, []); 

  const resetPassword = useCallback(async (token, newPasswordValue) => { 
    setLoading(true);
    setError(null);
    console.log('AuthContext: Reset password initiated for token:', token.substring(0, 10) + '...');
    try {
      const response = await api.post(`/api/auth/reset-password/${token}`, {
        password: newPasswordValue 
      });
      console.log('AuthContext: Password reset successful.');
      message.success(response.data?.message || 'Your password has been reset successfully!'); 
      navigate('/login', { replace: true }); 
      return response.data;
    } catch (error) {
      console.error('AuthContext: resetPassword error:', error.response?.data || error.message, error);
      setError(error.response?.data?.message || error.message);
      message.error(error.response?.data?.message || 'Failed to reset password.'); 
      throw error;
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const hasPermission = useCallback((resource, action, requiredLevel) => {
    if (!auth.user?.roleId) return false;
    
    const roleKey = Object.keys(ROLE_DEFINITIONS).find(
      key => ROLE_DEFINITIONS[key].id === auth.user.roleId
    );
    
    if (!roleKey) {
      log('warn', `Role ID ${auth.user.roleId} not found in definitions`);
      return false;
    }
    
    const permissionLevel = ROLE_DEFINITIONS[roleKey]?.permissions?.[resource]?.[action];
    const hasAccess = permissionLevel >= requiredLevel;
    
    log('debug', `Permission check`, {
      user: auth.user.id,
      role: ROLE_DEFINITIONS[roleKey].name,
      resource,
      action,
      requiredLevel,
      permissionLevel,
      hasAccess
    });
    
    return hasAccess;
  }, [auth.user, log]);

  const hasRole = useCallback((roleNames) => {
    if (!auth.user || !auth.user.roleId) return false;
    const userRoleDef = Object.values(ROLE_DEFINITIONS).find(def => def.id === auth.user.roleId);
    if (!userRoleDef) return false;
    const requiredRolesArray = Array.isArray(roleNames) ? roleNames : [roleNames];
    return requiredRolesArray.includes(userRoleDef.name);
  }, [auth.user]);

  const hasAnyRole = useCallback((roleNames) => {
    if (!auth.user || !auth.user.roleId) return false;
    const userRoleDef = Object.values(ROLE_DEFINITIONS).find(def => def.id === auth.user.roleId);
    if (!userRoleDef) return false;
    const requiredRolesArray = Array.isArray(roleNames) ? roleNames : [roleNames];
    return requiredRolesArray.some(role => userRoleDef.name === role);
  }, [auth.user]);

  const isSystemAdmin = useMemo(() => {
    if (!auth.user || !auth.user.roleId) return false;
    return auth.user.roleId === ROLE_DEFINITIONS.SYSTEM_ADMIN.id;
  }, [auth.user]);

  const hasOrganizationAccess = useCallback((requiredOrgId) => {
    if (!auth.user) return false;
    if (auth.user.roleId === ROLE_DEFINITIONS.SYSTEM_ADMIN.id) return true;
    if (!auth.user.organizationId || !requiredOrgId) return false;
    return auth.user.organizationId === requiredOrgId;
  }, [auth.user]);

  const getUserRoleDefinition = useMemo(() => auth.user ?
    Object.values(ROLE_DEFINITIONS).find(def => def.id === auth.user.roleId) : null,
    [auth.user]);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      checkAuth(true);
    } else {
      setLoading(false);
      setInitialized(true);
    }

    return () => {
      if (refreshTokenTimeout.current) {
        clearTimeout(refreshTokenTimeout.current);
      }
    };
  }, [checkAuth]);

  const contextValue = useMemo(() => ({
    user: auth.user,
    isAuthenticated: auth.isAuthenticated,
    loading,
    initialized,
    error,
    tokenExpiry,
    // 🆕 Client Portal
    eucContracts,
    hasClientPortalAccess: eucContracts.length > 0,
    fetchClientPortalContracts,
    // Existing
    login,
    logout,
    clearAuth: () => clearAuth({ redirect: true }),
    checkAuth,
    refreshToken,
    fetchUserProfile,
    requestPasswordReset,
    resetPassword,
    hasRole,
    hasAnyRole,
    isSystemAdmin,
    hasOrganizationAccess,
    hasPermission,
    getUserRoleDefinition,
    ROLE_DEFINITIONS,
    ROLE_NAMES,
    ROLE_IDS,
    ROLES,
    PERMISSION_LEVELS
  }), [
    auth.user,
    auth.isAuthenticated,
    loading,
    initialized,
    error,
    tokenExpiry,
    // 🆕 Client Portal
    eucContracts,
    fetchClientPortalContracts,
    // Existing
    login,
    logout,
    clearAuth,
    checkAuth,
    refreshToken,
    fetchUserProfile,
    requestPasswordReset,
    resetPassword,
    hasRole,
    hasAnyRole,
    isSystemAdmin,
    hasOrganizationAccess,
    hasPermission,
    getUserRoleDefinition
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};